/**
 * Renders delivery lifecycle UI beside outbound OncoEMR chat bubbles.
 * @module taskMessageDeliveryAccessory
 */

import { useRef, useState } from "react";

import ToolTipUsingPortal from "../../../../../components/toolTipUsingPortal/toolTipUsingPortal";
import type { OncoemrThreadOutboxStatus } from "../../../../../data-model/oncoemrTaskMessagesModel";
import TaskMessageErrorIcon from "../../../../../svg/taskMessageErrorIcon";
import TaskMessagePendingIcon from "../../../../../svg/taskMessagePendingIcon";

/** Props for {@link TaskMessageDeliveryAccessory}. */
export interface TaskMessageDeliveryAccessoryProps {
  status: OncoemrThreadOutboxStatus;
  /** Shown in a tooltip when `status` is `failed`. */
  lastError?: string;
  /** Invoked when the user clicks Retry on a failed outbox row. */
  onRetry?: () => void;
}

/**
 * Returns the screen-reader label for an outbox delivery accessory.
 *
 * @param status - Outbox lifecycle (`pending`, `sending`, or `failed`).
 * @param lastError - Optional API error text when `status` is `failed`.
 * @returns Accessible label for Oasis `ChatMessage`.
 */
export const getTaskMessageDeliveryAccessoryAriaLabel = (
  status: OncoemrThreadOutboxStatus,
  lastError?: string,
): string => {
  if (status === "failed") {
    return lastError
      ? `Message failed to send: ${lastError}`
      : "Task message failed to send";
  }

  if (status === "sending" || status === "pending") {
    return "Sending task message";
  }

  return "Task message pending delivery";
};

/**
 * Renders status-driven delivery UI: error + Retry when failed; clock + Sending… otherwise.
 *
 * @param props - Outbox status, optional error text, and retry handler for failed rows.
 * @returns Accessory node for Oasis `ChatMessage`.
 */
export const TaskMessageDeliveryAccessory = ({
  status,
  lastError,
  onRetry,
}: TaskMessageDeliveryAccessoryProps) => {
  const errorIconRef = useRef<HTMLDivElement>(null);
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);

  if (status === "failed") {
    const tooltipText = lastError?.trim() || "Message failed to send.";

    return (
      <div className="flex items-center gap-2">
        <div
          ref={errorIconRef}
          className="shrink-0"
          onMouseEnter={() => setShowErrorTooltip(true)}
          onMouseLeave={() => setShowErrorTooltip(false)}
        >
          <TaskMessageErrorIcon className="shrink-0" />
          <ToolTipUsingPortal
            placement="left"
            showTooltip={showErrorTooltip}
            parentRef={errorIconRef}
            maxWidth="20rem"
            wrapText
          >
            <span className="text-tiny text-gray-800">{tooltipText}</span>
          </ToolTipUsingPortal>
        </div>
        {onRetry ? (
          <button
            type="button"
            className="font-lato shrink-0 text-[0.75rem] font-bold leading-[1.125rem] text-[#df4646] hover:underline"
            onClick={onRetry}
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="font-lato flex items-center gap-1 text-[0.75rem] leading-[1.125rem] text-primaryGray-7">
      <TaskMessagePendingIcon className="shrink-0" />
    </div>
  );
};
